import apiClient from "../../api/api";

/**
 * @description Fetches a paginated list of talents.
 *
 * @async
 * @function requestGetTalents
 * @returns {Promise<any>} A promise that resolves with the talents list data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetTalents = async() => {
    const params = {
        page: 1,
        limit:100
    }
    const getTalentsResponse = await apiClient.get(import.meta.env.VITE_BATE_TALENT_URL, { params })

    return getTalentsResponse.data
}


/**
 * @description Fetches a talent by its unique identifier.
 *
 * @async
 * @function requestGetTalentById
 * @param {number} talentId - The unique identifier of the talent.
 *
 * @returns {Promise<any>} A promise that resolves with the talent data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetTalentById = async(talentId) => {
    const getTalentResponse = await apiClient.get(import.meta.env.VITE_BATE_TALENT_URL+`/${talentId}`)
    return getTalentResponse.data
}

/**
 * @description Creates a new talent.
 *
 * @async
 * @function requestCreateTalent
 * @param {Object} talent - The talent payload to be created.
 *
 * @returns {Promise<any>} A promise that resolves with the created talent response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestCreateTalent = async(talent) => {
    const createTalentResponse = await apiClient.post(import.meta.env.VITE_BATE_TALENT_URL, talent, {successToast:"Talent created"})

    return createTalentResponse
}

/**
 * @description Updates an existing talent.
 *
 * @async
 * @function requestUpdateTalent
 * @param {number} talentId - The unique identifier of the talent.
 * @param {Object} data - The payload containing the updated talent data.
 *
 * @returns {Promise<any>} A promise that resolves with the update response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestUpdateTalent = async(talentId, data) => {
    const updateTalentReponse = await apiClient.patch(import.meta.env.VITE_BATE_TALENT_URL+`/${talentId}`, data, {successToast:"Talent updated"})

    return updateTalentReponse
}

/**
 * @description Uploads or updates a talent's resume.
 *
 * @async
 * @function requestUploadTalentResume
 * @param {number} talentId - The unique identifier of the talent.
 * @param {Object|FormData} resume - The resume data to be uploaded.
 *
 * @returns {Promise<any>} A promise that resolves with the upload response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestUploadTalentResume = async(talentId, resume) => {
    const uploadResumeResponse = await apiClient.patch(import.meta.env.VITE_BATE_TALENT_URL+`/${talentId}/resume`, resume, {successToast:"Talent resume uploaded"})

    return uploadResumeResponse
}

/**
 * @description Fetches the resume of a specific talent.
 *
 * @async
 * @function requestGetTalentResume
 * @param {number} talentId - The unique identifier of the talent.
 *
 * @returns {Promise<any>} A promise that resolves with the resume data.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestGetTalentResume = async(talentId) => {
    const getResumeResponse = await apiClient.get(import.meta.env.VITE_BATE_TALENT_URL+`/${talentId}/resume`, {responseType: "blob"})

    return getResumeResponse
}

/**
 * @description Deletes a talent by its unique identifier.
 *
 * @async
 * @function requestDeleteTalent
 * @param {number} talentId - The unique identifier of the talent.
 *
 * @returns {Promise<any>} A promise that resolves with the delete response.
 *
 * @throws {Error} Throws an error if the API request fails.
 */
export const requestDeleteTalent = async(talentId) => {
    const deleteTalentRequest = await apiClient.delete(import.meta.env.VITE_BATE_TALENT_URL+`/${talentId}`, {successToast:"Talet deleted"})

    return deleteTalentRequest
}

// types ("SKILL", "LANGUAGE")
export const requestGetTalentsCatalog = async(type) => {
    const params = {
        type
    }
    const getCatalogResponse = await apiClient.get(import.meta.env.VITE_TALENTS_CATALOG_URL, { params })

    return getCatalogResponse.data
}